// Ficha 02 — KYC (Metamap embebido / QR) + ocupación. Look 1:1 con el mock
// `wizard/02-kyc-metamap.html`: una WizardCard por estado, con el icono redondo de 56 px, título de
// 15 px, subtítulo muted centrado (max-w-md) y los bloques de detalle alineados a la izquierda —
// lista de lo que verifica el proveedor (ready), fases de captura (waiting), resumen del INE +
// bloque de domicilio + micro-paso de ocupación e ingreso (verified) y tarjeta de motivo (failed).
// Bloqueante: sin KYC no hay venta, ni de contado. Estados ready → waiting (poll 3–5 s AL WIZARD,
// D20) → verified → ocupación → espera de la cuenta → 03. Reglas puente D25: el front NUNCA llama a
// Metamap API ni a Fiado; solo el widget SaaS.
//
// La espera de la cuenta es la última pantalla de este paso, no un paso nuevo: sin cuenta
// PagoConFiado no se avanza al catálogo, y el bloqueo es duro (no hay atajo para seguir sin ella).
//
// Cierra TD-F2-WIZ-004: `STORE_DEVICE` monta el `<metamap-button>` REAL dentro de un modal (no
// headless) con el `clientId`/`flowId`/`metadata` que devuelve `kyc/prepare` — el front no arma ni
// recalcula ninguno de los tres (no conoce `peopleId` ni `typeOfDirectoryId`).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, ArrowRight, Check, IdCard, ReceiptText, ScanFace, X } from 'lucide-react';
import { Badge, Button, Loading, Select } from '@/kit';
import { KycAttemptStageEnum } from '@fiado/type-kit/bin/retailWizard/enums/KycAttemptStageEnum';
import { ApiError } from '@/api/errors';
import { cn } from '@/lib/cn';
import {
    WIZARD_KEYS,
    useAccountStatus,
    useCancelSession,
    useKycAttemptPoll,
    useProfile,
    useSetOccupation,
    useWizardSession,
} from '../api/useWizard';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardBackLink, WizardFootNav } from '../components/WizardFootNav';
import { AddressBlock } from '../components/AddressBlock';
import {
    AttemptNotices,
    AttemptSdkModal,
    MetamapAttemptPanel,
    SwitchConfirmDialog,
} from '../components/MetamapAttemptPanel';
import { ProofOfAddressScreen } from '../components/ProofOfAddressScreen';
import { QueryErrorNotice } from '../components/QueryErrorNotice';
import { KycFailureScreen } from '../components/KycFailureScreen';
import { SaleClosedNotice } from '../components/SaleClosedNotice';
import { KYC_FAILURE_COPY, isSessionConflict, prepareFailureOf } from '../kycFailure';
import { wizardErrorMsg } from '../api/wizardError';
import { ModalityPicker, ScreenIntro, StateIcon, WaitingIcon } from '../components/KycScreenBits';
import { EstacionesKyc, KycStationChips } from '../components/EstacionesKyc';
import { useMetamapAttempt } from '../api/useMetamapAttempt';
import { attemptProofResult } from '../proofOfAddress';
import { sigueEsperandoElKyc } from '../kycEspera';
import {
    accountAlreadyOpen,
    accountBlocksTheSale,
    accountWaitTimedOut,
    blockedNoticeOf,
    exitOfferedWhileOpening,
} from '../accountWait';
import {
    ACCOUNT_OPENING_COMPLETE_FAILED,
    ACCOUNT_OPENING_KYC_EYEBROW,
    ACCOUNT_OPENING_MISSING_FIELD,
    ACCOUNT_OPENING_PROFILE_HINT,
    ACCOUNT_OPENING_PROFILE_SUB,
    ACCOUNT_OPENING_READY_SUB,
    ACCOUNT_OPENING_READY_TITLE,
    ACCOUNT_OPENING_SUBMIT,
    hasAccountOpeningMark,
} from '../accountOpening';
import { accountOpeningFailureOf, type AccountOpeningFailure } from '../accountOpeningErrors';
import { ProfileQuestions, type ProfileQuestionKey } from '../components/ProfileQuestions';
import {
    openingAnswersComplete,
    profileAnswerOf,
    toSetOccupationInput,
    type ProfileAnswers,
} from '../components/profileAnswers';
import { useAccountOpeningCompletion } from '../api/useAccountOpeningCompletion';
import { stepsFor } from '../steps';
import {
    ACCOUNT_OPENING_SUB,
    ACCOUNT_OPENING_TITLE,
    BLOCKED_SCRIPT_LABEL,
    blockedCopy,
} from '../blocked';
import { step02Request, step02RequestCopy, type Step02Request } from '../step02Request';
import { useWaitTimer } from '../api/useWaitTimer';
import type {
    AccountStatusResult,
    KycModality,
    KycSubChecks,
    MonthlyIncome,
    Occupation,
} from '../types';
import { MONTHLY_INCOME_OPTIONS, OCCUPATION_OPTIONS } from '../profileCatalog';

/**
 * Nombre legible del documento con el que se verificó al cliente.
 *
 * El proveedor manda su catálogo crudo (`national-id`, `passport`, …). Un código sin traducir no le
 * dice nada al vendedor, y un tipo que no conocemos se muestra tal cual antes que esconderlo.
 *
 * Sin tipo devuelve `Identificación`: el flujo acepta más de un documento, así que nombrarlo sin
 * saber cuál fue —como se hacía con "INE"— es peor que no nombrarlo.
 */
function nombreDelDocumento(tipo?: string): string {
    switch (tipo) {
        case 'national-id':
            return 'INE / IFE';
        case 'passport':
            return 'Pasaporte';
        case 'driving-license':
            return 'Licencia de conducir';
        case 'residence-permit':
            return 'Residencia';
        case undefined:
        case '':
            return 'Identificación';
        default:
            return tipo;
    }
}

export function KycStep({ sessionId }: { sessionId: string }) {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { data: session } = useWizardSession(sessionId);
    const setOccupation = useSetOccupation(sessionId);
    const cancelSession = useCancelSession();
    const isAccountOpening = !!session && hasAccountOpeningMark(session);

    const kycStatus = session?.kyc.status ?? 'READY';
    const esperandoPorEstado = sigueEsperandoElKyc(kycStatus, session?.state);
    const isWaiting = esperandoPorEstado;
    const kycFlow = session?.kyc.route === 'FACEMATCH' ? 'facematch' : 'identity';

    // El expediente ya viajó ⇒ la emisión arrancó y hay que esperarla. Se mira la SESIÓN además del
    // envío local para que recargar la pestaña no devuelva al formulario que ya se mandó.
    //
    // Hacen falta los DOS campos: viajan en la misma llamada, así que uno solo significa expediente
    // a medias (una sesión anterior al ingreso mensual) y ahí lo correcto es dejarlo completar.
    const [occupationSent, setOccupationSent] = useState(false);
    const profileOnFile = Boolean(session?.occupation) && Boolean(session?.monthlyIncome);
    const waitingForAccount = occupationSent || profileOnFile;

    // La cuenta YA existe ⇒ el paso 02 no tiene expediente que completar: no se piden domicilio,
    // ocupación ni ingreso. Mismo predicado que el silo (`isAccountReady`).
    const accountSettled = accountAlreadyOpen(session?.accountStatus);

    // Sin `restart`: la venta trabada ya no ofrece «volver a consultar». El reintento vive en el
    // silo, y desde aquí la salida es retomarla más tarde desde Prospectos.
    const [msWaitingForAccount] = useWaitTimer(waitingForAccount);
    // El tope decide qué PANTALLA se ve, no si se sigue preguntando: la cuenta que abre tarde
    // destraba la venta sola.
    const accountWaitExpired = accountWaitTimedOut(msWaitingForAccount);

    // El domicilio y las preguntas viven en la ÚLTIMA pantalla del paso, así que el perfil solo se
    // pide ahí: el KYC y la cuenta pollean cada pocos segundos y esto no cambia con sus ticks.
    const showsSummary =
        session?.state !== 'PAUSED_CURP_CONFLICT' &&
        !esperandoPorEstado &&
        kycStatus !== 'READY' &&
        kycStatus !== 'FAILED' &&
        kycStatus !== 'PLD' &&
        !waitingForAccount &&
        !accountSettled;

    // La cuenta existe ⇒ el paso 02 terminó: es el ÚNICO camino al 03. El poll solo cuenta durante la
    // espera, porque en facematch la cuenta existe desde antes del KYC.
    const accountReadyWith = (status: AccountStatusResult | undefined): boolean =>
        accountSettled || (waitingForAccount && status?.accountReady === true);
    // Al retomar desde Prospectos el `blocked` del silo se evalúa antes que la ocupación, así que la
    // venta trabada vuelve a su aviso. `canStillCollect` = al paso le queda el comprobante o el perfil.
    const saleBlockedWith = (status: AccountStatusResult | undefined, error: unknown): boolean =>
        accountBlocksTheSale({
            blocked: session?.blocked,
            waiting: waitingForAccount,
            accountReady: accountReadyWith(status),
            lookupError: error,
            status,
            msWaiting: msWaitingForAccount,
            waitExpired: accountWaitExpired,
            canStillCollect: showsSummary,
        });
    const accountPoll = useAccountStatus(
        sessionId,
        waitingForAccount,
        msWaitingForAccount,
        (status, error) => blockedNoticeOf(saleBlockedWith(status, error), session?.blocked),
    );
    const account = accountPoll.data;
    const accountIsReady = accountReadyWith(account);
    // Con la cuenta lista el paso aún puede deber el domicilio: el poll y el comprobante siguen vivos.
    const canAskForAddress = showsSummary || accountIsReady;

    // Solo selfie en FAILED: la pantalla sale del `kycFailure.code`, que solo trae `kyc/status`.
    const selfieFailed = session?.kyc.route === 'FACEMATCH' && kycStatus === 'FAILED';
    // El poll sigue el intento del flujo a la vista: el comprobante si falta, si no la identidad o la selfie.
    const kycPoll = useKycAttemptPoll(sessionId, {
        kycFlow,
        kycWaiting: esperandoPorEstado || kycStatus === 'REVIEW',
        canAskForAddress,
        awaitingFailureCode: selfieFailed,
    });
    /** Repregunta ya por el requisito y reinicia el tope, sin esperar al siguiente tick del poll. */
    const recheckProof = async (): Promise<void> => {
        kycPoll.restartAttemptClock();
        await kycPoll.refetch();
    };

    const {
        data: profile,
        isLoading: profileLoading,
        isError: profileFailed,
        isFetching: profileFetching,
        refetch: refetchProfile,
    } = useProfile(sessionId, showsSummary);

    // 🔴 GATE. Lo decide el SILO con el mismo predicado que `setOccupation` va a exigir. Antes se
    // gateaba con la lista de domicilios, que responde otra pregunta: por ahí entraba el 409.
    // Ausente (despliegue viejo, sin candado del otro lado) NO bloquea: la pantalla no tendría salida.
    const addressRequired = kycPoll.data?.addressRequired === true;

    // El comprobante es la pantalla que el vendedor tiene enfrente ⇒ manda sobre la identidad del
    // paso (título, chip e icono). `canAskForAddress` es la condición que deja llegar hasta ella.
    const askingForProof = addressRequired && canAskForAddress;
    const request = step02Request(session?.kyc.route, askingForProof);

    /** Lo que el silo dice del domicilio: cambia cuando el comprobante se mueve. */
    const addressFingerprint = [
        String(kycPoll.data?.addressRequired),
        kycPoll.data?.proofOfAddress?.verificationId ?? '',
        kycPoll.data?.proofOfAddress?.status ?? '',
    ].join('·');
    const lastAddressFingerprint = useRef(addressFingerprint);
    // 🔴 El perfil se cachea 60 s y el comprobante escribe el domicilio POR FUERA de esta pestaña.
    // Sin releerlo, la pantalla siguiente afirma que falta el domicilio que el silo ya entrega — y su
    // buscador invita a capturar un duplicado sobre el que ya está.
    useEffect(() => {
        if (lastAddressFingerprint.current === addressFingerprint) return;
        lastAddressFingerprint.current = addressFingerprint;
        void qc.invalidateQueries({ queryKey: WIZARD_KEYS.profile(sessionId) });
    }, [addressFingerprint, qc, sessionId]);

    // Los tres campos del expediente. `null` = el vendedor no los tocó, así que vale lo que la sesión
    // ya tiene registrado: volver al paso 02 muestra lo capturado, no un formulario en blanco.
    const [occInput, setOcc] = useState<Occupation | null>(null);
    const [occOtherInput, setOccOther] = useState<string | null>(null);
    const [incomeInput, setIncome] = useState<MonthlyIncome | null>(null);
    const occ: Occupation | '' = occInput ?? session?.occupation?.code ?? '';
    const occOther = occOtherInput ?? session?.occupation?.otherText ?? '';
    const income: MonthlyIncome | '' = incomeInput ?? session?.monthlyIncome ?? '';
    // Apertura: los 4 MXN_* que la venta pregunta hasta la Bienvenida, y el rechazo tipado del silo.
    const [profileInput, setProfileInput] = useState<ProfileAnswers>({});
    const [openingFailure, setOpeningFailure] = useState<AccountOpeningFailure | null>(null);
    /** «Intentar de nuevo» tras un FAILED: el vendedor vuelve a elegir tablet o QR. */
    const [pickingRetry, setPickingRetry] = useState(false);

    // El avance por estación viaja en el POLL, no en la sesión: es dato de UX y el silo no lo espeja
    // en la fila. TanStack conserva la última data cuando `enabled` pasa a false, así que las
    // casillas siguen visibles al llegar el veredicto y el vendedor ve QUÉ estación falló.
    const subChecks = kycPoll.data?.subChecks;
    /**
     * El cliente ya tiene cuenta Pago Confiado: solo se toma una selfie, sin INE.
     *
     * Se compara contra `FACEMATCH` en vez de preguntar por la negativa: mientras la bifurcación no
     * se resolvió la ruta llega `undefined`, y ahí lo correcto es la pantalla completa —anunciar de
     * más es preferible a que el cliente llegue sin su INE.
     */
    const esFacematch = session?.kyc.route === 'FACEMATCH';

    // La sesión se relee cuando el poll cambia de estatus, no en cada vuelta.
    const polledStatus = kycPoll.data?.status;
    useEffect(() => {
        if (polledStatus === undefined || polledStatus === session?.kyc.status) return;
        void qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
    }, [polledStatus, session?.kyc.status, qc, sessionId]);

    // Con cuenta lista pero sin nivel resuelto, pasa primero por 05 (score adelantado, visible) antes
    // del catálogo — para marketing y para marcar affordableOnCredit desde el 03. Si ya tiene nivel
    // (reanudación tras verlo una vez), va directo al catálogo sin repetir la pantalla.
    const nextStepAfterAccount = session?.loan?.level ? 'product' : 'score';
    // Sin domicilio no se avanza: el silo rechaza el score y la reanudación volvería al comprobante.
    const addressPending = kycPoll.isPending || addressRequired;
    useEffect(() => {
        if (isAccountOpening || !accountIsReady || addressPending) return;
        void navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: nextStepAfterAccount },
        });
    }, [
        isAccountOpening,
        accountIsReady,
        addressPending,
        navigate,
        sessionId,
        nextStepAfterAccount,
    ]);
    /** Identidad vigente o 409 KYC_ALREADY_VERIFIED: nada que capturar, se avanza si se puede. */
    const continueWithVerifiedIdentity = async (): Promise<void> => {
        setPickingRetry(false);
        // Sin domicilio se queda: la sesión VERIFIED pinta el comprobante.
        const fresh = await kycPoll.refetch();
        if (fresh.data?.addressRequired === true) return;
        // Sin cuenta el silo rechaza el score: se queda y el paso pide lo que falta.
        if (!accountSettled) {
            const freshAccount = await accountPoll.refetch();
            if (freshAccount.data?.accountReady !== true) return;
        }
        // En la apertura el cierre lo hace el efecto de la cuenta lista.
        if (isAccountOpening) return;
        toast.success('Cliente con identidad vigente — continuamos.');
        await navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: nextStepAfterAccount },
        });
    };
    /** El expediente ya tenía domicilio: se relee el gate en vez de pintar un QR. */
    const refreshAddressGate = (): void => {
        void qc.invalidateQueries({ queryKey: WIZARD_KEYS.kyc(sessionId) });
        void qc.invalidateQueries({ queryKey: WIZARD_KEYS.profile(sessionId) });
        toast.success('El cliente ya tiene un domicilio registrado.');
    };
    const kycAttempt = useMetamapAttempt(sessionId, kycFlow, {
        poll: kycPoll,
        onAlreadyDone: continueWithVerifiedIdentity,
    });
    const proofAttempt = useMetamapAttempt(sessionId, 'proofOfAddress', {
        poll: kycPoll,
        onAlreadyDone: refreshAddressGate,
    });
    // El veredicto cuenta solo si es del intento ligado, no de otro que siga en el poll.
    const proofResult = attemptProofResult(kycPoll.data?.proofOfAddress, proofAttempt.progress);
    // El error del último prepare es más nuevo que el poll; si no, manda el `kycFailure.code`.
    const failure = prepareFailureOf(kycAttempt.error) ?? kycPoll.data?.failure ?? null;
    // Un 409 de sesión (distinto de KYC_ALREADY_VERIFIED) se decide releyendo el motivo vigente.
    const prepareError = kycAttempt.error;
    useEffect(() => {
        if (!isSessionConflict(prepareError)) return;
        void qc.invalidateQueries({ queryKey: WIZARD_KEYS.kyc(sessionId) });
        void qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
    }, [prepareError, qc, sessionId]);
    const startKyc = (modality: KycModality): void => {
        setPickingRetry(false);
        kycAttempt.launch(modality);
    };

    // Apertura: con la cuenta OPEN o LINKED se cierra en el silo; el 409 ACCOUNT_NOT_READY reintenta.
    const openingSteps = stepsFor(session);
    const nextOpeningStep =
        openingSteps[openingSteps.findIndex((s) => s.slug === 'kyc') + 1]?.slug ?? 'delivery';
    const completion = useAccountOpeningCompletion(sessionId, {
        ready: isAccountOpening && accountIsReady,
        addressPending,
        nextStep: nextOpeningStep,
    });

    const saleBlocked = saleBlockedWith(account, accountPoll.error);

    // La cuenta abrió con la venta trabada: se relee la sesión para que el `blocked` y el
    // `accountStatus` que arrastra no sigan pintando el letrero.
    const unblockedByAccount = accountIsReady && !!session?.blocked;
    useEffect(() => {
        if (!unblockedByAccount) return;
        void qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
    }, [unblockedByAccount, qc, sessionId]);

    if (!session) return <Loading size="lg" />;

    /** Relee `kyc/status` y la sesión para decidir por el `kycFailure.code` vigente. */
    const rereadKyc = (): void => {
        void qc.invalidateQueries({ queryKey: WIZARD_KEYS.kyc(sessionId) });
        void qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
    };

    /** «Terminar»: cancela con el code como motivo si la sesión sigue abierta, y va a venta nueva. */
    const finish = async (cancelReason?: string): Promise<void> => {
        if (cancelReason) {
            try {
                await cancelSession.mutateAsync({ sessionId, reason: cancelReason });
            } catch (error) {
                // 409: la sesión ya está cerrada, que es lo que se buscaba.
                if (!(error instanceof ApiError && error.httpStatus === 409)) {
                    toast.error(wizardErrorMsg(error, 'No se pudo cerrar la venta.'));
                    return;
                }
            }
        }
        await navigate({ to: '/wizard' });
    };

    const finishOccupation = async (): Promise<void> => {
        if (!occ || !income) return;
        setOpeningFailure(null);
        try {
            await setOccupation.mutateAsync(
                toSetOccupationInput({
                    occupation: occ,
                    otherText: occOther,
                    income,
                    answers: profileInput,
                    accountOpening: isAccountOpening,
                }),
            );
        } catch (error) {
            // 400 con los campos faltantes y 422 por dinero ajeno: se quedan en pantalla.
            const opening = isAccountOpening ? accountOpeningFailureOf(error) : null;
            if (opening) {
                setOpeningFailure(opening);
                return;
            }
            // El silo manda sobre el domicilio. Se relee el POLL, que es de donde sale el gate: sin
            // eso la pantalla del comprobante no vuelve y el vendedor se queda sin qué hacer.
            if (error instanceof ApiError && error.code === 'ADDRESS_REQUIRED') {
                void qc.invalidateQueries({ queryKey: WIZARD_KEYS.kyc(sessionId) });
                void qc.invalidateQueries({ queryKey: WIZARD_KEYS.profile(sessionId) });
                toast.error('Falta el domicilio del cliente: captúralo antes de continuar.');
                return;
            }
            if (isSessionConflict(error)) rereadKyc();
            toast.error(error instanceof ApiError ? error.msg : 'No se pudo guardar el perfil.');
            return;
        }
        // Aquí NO se navega. La ocupación completa el expediente y con eso arranca la emisión de la
        // cuenta; el paso sigue en esta pantalla hasta que la cuenta exista de verdad.
        setOccupationSent(true);
    };

    /** Modal y confirmación del intento, montados una sola vez: el prepare pasa la sesión a WAITING. */
    const captureModal = (
        <>
            <AttemptSdkModal attempt={kycAttempt} title={step02RequestCopy(request).modalTitle} />
            <SwitchConfirmDialog attempt={kycAttempt} />
        </>
    );

    const failureScreen = (activeFailure: NonNullable<typeof failure>) => (
        <KycCard request={request} sessionId={sessionId} showExit={session.state !== 'CANCELLED'}>
            <KycFailureScreen
                failure={activeFailure}
                tabletHint={step02RequestCopy(request).tabletHint}
                phoneHint={step02RequestCopy(request).phoneHint}
                retrying={kycAttempt.isPending}
                finishing={cancelSession.isPending}
                onRetry={startKyc}
                onFinish={() => {
                    const cancels = KYC_FAILURE_COPY[activeFailure].action === 'FINISH_WITH_CANCEL';
                    void finish(cancels ? activeFailure : undefined);
                }}
            />
        </KycCard>
    );

    /** La CURP ya tiene cuenta con otro teléfono: la sesión no sigue. */
    const curpConflictScreen = (showExit: boolean) => (
        <KycCard request={request} sessionId={sessionId} showExit={showExit}>
            <div className="text-center">
                <StateIcon tone="danger">
                    <X className="size-7" aria-hidden />
                </StateIcon>
                <p className="mb-1 text-[15px] font-semibold">Esta persona ya está registrada</p>
                <p className="mx-auto max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                    Esta CURP ya tiene cuenta con otro teléfono. Inicia la venta con ese número.
                </p>
            </div>
        </KycCard>
    );

    /** El cierre de la apertura falló por algo distinto de la cuenta que aún no está lista. */
    const completeFailedNotice = completion.failed ? (
        <QueryErrorNotice
            title={ACCOUNT_OPENING_COMPLETE_FAILED}
            error={completion.error}
            fallback={ACCOUNT_OPENING_COMPLETE_FAILED}
            onRetry={completion.retry}
        />
    ) : null;

    /** La pantalla del estado vigente; el modal del SDK va fuera para no remontarse al cambiar. */
    const renderScreen = (): ReactNode => {
        // ── Sesión cerrada: el rechazo de la selfie y el CURP distinto tienen pantalla propia ──
        if (session.state === 'CANCELLED') {
            if (session.accountOpeningCancelReason === 'CURP_OF_OTHER_CUSTOMER') {
                return curpConflictScreen(false);
            }
            if (session.accountOpeningCancelReason === 'ALREADY_HAS_ACCOUNT') {
                return <SaleClosedNotice phone={session.phone} alreadyHasAccount />;
            }
            if (failure === 'FACEMATCH_REJECTED' || failure === 'FIADO_CURP_MISMATCH') {
                return failureScreen(failure);
            }
            if (kycPoll.isPending) return <Loading size="lg" />;
            return (
                <SaleClosedNotice
                    phone={session.phone}
                    expired={session.expired}
                    quoteDiscarded={session.quoteDiscarded}
                    takenOver={session.takenOverByOtherStore}
                    accountOpening={isAccountOpening}
                />
            );
        }

        // ── CURP de otro cliente: la sesión ya no puede seguir ───────────────────
        // Va antes que cualquier estado del KYC porque es terminal: la verificación pudo haber salido
        // perfecta y aun así la venta no continúa. Sin esta pantalla el vendedor se quedaba mirando la
        // espera del KYC para siempre, porque su estado nunca iba a cambiar.
        if (session.state === 'PAUSED_CURP_CONFLICT') return curpConflictScreen(true);

        // ── Cuenta lista pero sin domicilio: el comprobante va antes de avanzar ──
        if (accountIsReady && askingForProof) {
            return (
                <KycCard request={request} sessionId={sessionId}>
                    <ProofOfAddressScreen
                        sessionId={sessionId}
                        attempt={proofAttempt}
                        result={proofResult}
                        addressRequired
                        onRecheck={recheckProof}
                    />
                </KycCard>
            );
        }

        // ── La cuenta ya está: nada que completar ────────────────────────────────
        // Pantalla de paso, no de espera: el efecto de arriba ya está navegando al catálogo. Existe para
        // que el formulario no alcance a pintarse mientras tanto.
        if (accountSettled) {
            return (
                <KycCard request={request} sessionId={sessionId}>
                    <div className="text-center">
                        <StateIcon tone="success">
                            <Check className="size-7" aria-hidden />
                        </StateIcon>
                        <p className="mb-1 text-[15px] font-semibold">
                            {isAccountOpening
                                ? ACCOUNT_OPENING_READY_TITLE
                                : 'La cuenta del cliente ya está lista'}
                        </p>
                        <p className="mx-auto max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                            {isAccountOpening
                                ? ACCOUNT_OPENING_READY_SUB
                                : 'No hace falta capturar nada más aquí. Continuamos con la elección del equipo.'}
                        </p>
                        {completeFailedNotice}
                    </div>
                </KycCard>
            );
        }

        // ── La verificación no avanzó: el motivo manda sobre el estado del KYC ───
        if (failure) return failureScreen(failure);

        // ── Solo selfie en FAILED sin motivo leído: la pantalla del KYC completo no aplica ──
        if (selfieFailed) {
            if (kycPoll.isError) {
                return (
                    <KycCard request={request} sessionId={sessionId}>
                        <div className="text-center">
                            <KycPollErrorNotice error={kycPoll.error} />
                            <Button
                                variant="outline"
                                isLoading={kycPoll.isFetching}
                                onClick={() => {
                                    void kycPoll.refetch();
                                }}
                            >
                                Volver a consultar
                            </Button>
                        </div>
                    </KycCard>
                );
            }
            // Una lectura que ya ve el FAILED sin code es un motivo que no conocemos.
            if (kycPoll.data?.status === 'FAILED') return failureScreen('UNKNOWN');
            return <Loading size="lg" />;
        }

        // ── READY: elegir modalidad ──────────────────────────────────────────────
        if (kycStatus === 'READY') {
            return (
                <KycCard request={request} sessionId={sessionId}>
                    <div className="text-center">
                        <ScreenIntro
                            tone="info"
                            icon={<RequestIcon request={request} />}
                            title={step02RequestCopy(request).pickTitle}
                            sub={step02RequestCopy(request).pickSub}
                        />

                        {/* Las dos modalidades valen para las DOS rutas: el facematch también puede
                            hacerse desde el celular del cliente con el QR. */}
                        <ModalityPicker
                            tabletHint={step02RequestCopy(request).tabletHint}
                            phoneHint={step02RequestCopy(request).phoneHint}
                            loading={kycAttempt.isPending}
                            onPick={startKyc}
                        />

                        {/* Candados del silo (500 CONFIGURACION_FALTANTE, 409 identidad sin resolver). Se
                        muestran en pantalla: antes fallaba mudo y el cliente hacía el KYC para nada. */}
                        <PrepareErrorNotice error={kycAttempt.error} />

                        <AttemptNotices attempt={kycAttempt} copy={step02RequestCopy(request)} />

                        {/* Lo que se le va a revisar al cliente; en el facematch solo la cara.
                            🔴 Mismo candado que las estaciones: no se nombra la causa ni quién revisa. */}
                        <div className="mx-auto max-w-md text-left">
                            {esFacematch ? (
                                <>
                                    <CheckItem
                                        title="Coincidencia del rostro"
                                        description="Compara la selfie con la foto que el cliente ya registró."
                                    />
                                    <CheckItem
                                        title="Persona en vivo"
                                        description="Confirma que la persona está frente a la cámara."
                                    />
                                </>
                            ) : (
                                <>
                                    <CheckItem
                                        title="Validación del documento"
                                        description="CURP, fecha de nacimiento y datos personales."
                                    />
                                    <CheckItem
                                        title="Verificaciones de seguridad"
                                        description="Comprueba que el documento no esté manipulado."
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </KycCard>
            );
        }

        // ── WAITING: el panel del intento manda; las estaciones van debajo ──
        if (isWaiting) {
            return (
                <KycCard request={request} sessionId={sessionId}>
                    <div className="text-center">
                        <MetamapAttemptPanel attempt={kycAttempt} copy={step02RequestCopy(request)}>
                            <PrepareErrorNotice error={kycAttempt.error} />
                            <KycPollErrorNotice error={kycPoll.error} />
                            <WaitingStations
                                stage={kycAttempt.stage}
                                subChecks={subChecks}
                                isFacematch={esFacematch}
                            />
                        </MetamapAttemptPanel>
                    </div>
                </KycCard>
            );
        }

        // ── FAILED / PLD ─────────────────────────────────────────────────────────
        if (kycStatus === 'FAILED' || kycStatus === 'PLD') {
            return (
                <KycCard
                    request={request}
                    sessionId={sessionId}
                    foot={
                        kycStatus === 'FAILED' && !pickingRetry ? (
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setPickingRetry(true);
                                }}
                            >
                                Intentar de nuevo
                            </Button>
                        ) : undefined
                    }
                >
                    <div className="text-center">
                        <StateIcon tone="danger">
                            <X className="size-7" aria-hidden />
                        </StateIcon>
                        <p className="mb-1 text-[15px] font-semibold">Verificación no aprobada</p>
                        <p className="mx-auto mb-5 max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                            Sin verificación no hay venta. Intenta de nuevo con buena luz.
                        </p>

                        <div className="mx-auto max-w-md rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-left">
                            <p className="mb-1 text-[12px] font-medium text-destructive">
                                Motivo del rechazo
                            </p>
                            <p className="text-[12px] leading-relaxed text-destructive">
                                {session.kyc.failureReason ?? 'Intenta de nuevo.'}
                            </p>
                        </div>

                        {kycStatus === 'FAILED' && pickingRetry && (
                            <div className="mt-5">
                                <ModalityPicker
                                    tabletHint={step02RequestCopy(request).tabletHint}
                                    phoneHint={step02RequestCopy(request).phoneHint}
                                    loading={kycAttempt.isPending}
                                    onPick={startKyc}
                                />
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setPickingRetry(false);
                                    }}
                                >
                                    Volver
                                </Button>
                            </div>
                        )}

                        {/* El motivo del silo es una frase; las estaciones dicen QUÉ falló. Sirve para
                            decidir si vale la pena reintentar (mala foto) o no (menor de edad). */}
                        <div className="mt-4">
                            <EstacionesKyc subChecks={subChecks} esFacematch={esFacematch} />
                        </div>

                        <div className="mt-4">
                            <PrepareErrorNotice error={kycAttempt.error} />
                        </div>
                    </div>
                </KycCard>
            );
        }

        // ── La venta se trabó esperando la cuenta ────────────────────────────────
        // Dos avisos: el que sigue esperando y el que ya no. Ninguno nombra el motivo —eso es delito—,
        // pero el que ya se resolvió tiene que decir que no continúa o el vendedor espera de más.
        if (saleBlocked) {
            const copy = blockedCopy(session.blocked?.category);
            return (
                <KycCard request={request} sessionId={sessionId}>
                    <div className="text-center">
                        <ScreenIntro
                            tone="warning"
                            icon={<AlertTriangle className="size-7" aria-hidden />}
                            title={copy.title}
                            sub={copy.sub}
                        />

                        {/* El vendedor tiene al cliente enfrente y necesita decir algo. Sin guion cada
                        quien improvisa, y ahí es donde alguien menciona una lista. */}
                        <div className="mx-auto mb-5 max-w-md rounded-lg border border-border bg-muted/30 p-4 text-left">
                            <p className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                                {BLOCKED_SCRIPT_LABEL}
                            </p>
                            <p className="text-[12.5px] leading-relaxed">{copy.script}</p>
                        </div>
                    </div>
                </KycCard>
            );
        }

        // ── Esperando a que exista la cuenta ─────────────────────────────────────
        // Última pantalla del 02, sin desglose de etapas: con 13 s de mediana medidos en Dev, el avance
        // desaparece antes de que el vendedor termine de leerlo.
        if (waitingForAccount) {
            return (
                <KycCard
                    request={request}
                    sessionId={sessionId}
                    showExit={exitOfferedWhileOpening(msWaitingForAccount)}
                >
                    <div className="text-center">
                        <WaitingIcon />
                        <p className="mb-1 text-[15px] font-semibold">{ACCOUNT_OPENING_TITLE}</p>
                        <p className="mx-auto mb-5 max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                            {ACCOUNT_OPENING_SUB}
                        </p>

                        {/* Una falla transitoria (5xx, red) no corta el poll, pero se dice: si no, el
                        vendedor lee "estamos abriendo" mientras nadie está preguntando nada. */}
                        <AccountErrorNotice error={accountPoll.error} />
                        {completeFailedNotice}
                    </div>
                </KycCard>
            );
        }

        // ── VERIFIED / MANUAL / REVIEW: resumen + ocupación ──────────────────────
        // El resumen viaja en el POLL, no en la sesión: el silo es PII-zero y no persiste identidad ni
        // domicilio — los lee del customer al responder. Se prefiere lo del poll y la sesión queda de
        // respaldo por si TanStack ya descartó la última data del poll al desmontarse.
        const v = kycPoll.data?.verified ?? session.kyc.verified;
        // Los DOS datos son obligatorios: con uno solo el expediente Nivel 1 queda a medias, y el paso
        // 02 es la única oportunidad de escribirlos (después Fiado rechaza todo update de agente).
        const openingAnswersReady = openingAnswersComplete(profileInput);
        const profileReady =
            !!occ &&
            (occ !== 'OTHER' || !!occOther.trim()) &&
            !!income &&
            (!isAccountOpening || openingAnswersReady);
        const openingAnswer = (k: ProfileQuestionKey): string =>
            profileAnswerOf(k, { occupation: occ, income, answers: profileInput });
        const changeOpeningAnswer = (k: ProfileQuestionKey, answer: string): void => {
            if (k === 'occupation') setOcc(answer as Occupation);
            else if (k === 'monthlyIncome') setIncome(answer as MonthlyIncome);
            else setProfileInput((current) => ({ ...current, [k]: answer }));
        };
        const occupationOtherInput = occ === 'OTHER' && (
            <input
                value={occOther}
                maxLength={60}
                onChange={(e) => {
                    setOccOther(e.target.value);
                }}
                placeholder="Describe la ocupación (máx. 60 caracteres)"
                aria-label="Describe la ocupación"
                className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-[13px] outline-none transition-colors focus:border-ring"
            />
        );

        // ── El comprobante de domicilio, en pantalla propia y ANTES de las preguntas ──
        // Solo se pide si el silo dice que falta: con INE el expediente ya lo tiene y se salta derecho a
        // la confirmación. Se sale con un comprobante procesado o con el domicilio capturado a mano.
        if (askingForProof) {
            return (
                <KycCard request={request} sessionId={sessionId}>
                    <ProofOfAddressScreen
                        sessionId={sessionId}
                        attempt={proofAttempt}
                        result={proofResult}
                        addressRequired
                        onRecheck={recheckProof}
                    />
                </KycCard>
            );
        }
        if (profileLoading) {
            return (
                <KycCard request={request} sessionId={sessionId}>
                    <Loading size="lg" />
                </KycCard>
            );
        }
        // 🔴 Que el expediente no se pueda LEER no es que el cliente no tenga domicilio. Con la lista
        // vacía por un 500, la pantalla afirmaría que le falta lo que ya dio.
        if (profileFailed || !profile) {
            return (
                <KycCard request={request} sessionId={sessionId}>
                    <div className="text-center">
                        <StateIcon tone="warning">
                            <AlertTriangle className="size-7" aria-hidden />
                        </StateIcon>
                        <p className="mb-1 text-[15px] font-semibold">
                            No se pudo leer el expediente del cliente
                        </p>
                        <p className="mx-auto mb-5 max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                            No sabemos qué domicilio tiene registrado, así que no podemos seguir sin
                            arriesgarnos a pedirle un comprobante que ya dio. Vuelve a consultar; si
                            sigue igual, repórtalo a soporte con el número de esta sesión.
                        </p>

                        <Button
                            variant="outline"
                            isLoading={profileFetching}
                            onClick={() => void refetchProfile()}
                        >
                            Volver a consultar
                        </Button>
                    </div>
                </KycCard>
            );
        }

        /** Domicilios que Fiado YA tiene: el del INE viajó al expediente al verificarse el KYC. */
        const savedAddresses = profile.domicilios;

        return (
            <KycCard
                request={request}
                sessionId={sessionId}
                foot={
                    <Button
                        disabled={!profileReady}
                        isLoading={setOccupation.isPending}
                        rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                        onClick={() => void finishOccupation()}
                    >
                        {isAccountOpening ? ACCOUNT_OPENING_SUBMIT : 'Continuar'}
                    </Button>
                }
            >
                <div className="text-center">
                    <StateIcon tone="success">
                        <Check className="size-7" aria-hidden />
                    </StateIcon>
                    <p className="mb-1 text-[15px] font-semibold">Identidad verificada</p>
                    <p className="mx-auto mb-5 max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                        {isAccountOpening
                            ? ACCOUNT_OPENING_PROFILE_SUB
                            : 'Completa ocupación e ingreso para abrir su cuenta.'}
                    </p>

                    {/* 🔴 Misma fórmula neutra que las estaciones: `REVIEW` es el desenlace de la
                    consulta de listas y el cliente lee esta pantalla de frente. */}
                    {kycStatus === 'REVIEW' && (
                        <div className="mx-auto mb-4 max-w-md rounded-md border border-warning-soft-border bg-warning-soft px-3 py-2 text-left text-[12px] text-warning">
                            La cuenta quedó en revisión adicional — la venta continúa.
                        </div>
                    )}

                    {/* Resumen de lo que devolvió el proveedor + estado de la cuenta Pago Confiado. */}
                    {v && (
                        <div className="mx-auto mb-4 max-w-md rounded-lg border border-border bg-card p-4 text-left">
                            <dl className="flex flex-col gap-2">
                                <SummaryRow label="Nombre" value={v.fullName} />
                                <SummaryRow label="CURP" value={v.curp} mono />
                                <SummaryRow
                                    label="Nacimiento"
                                    value={v.birthDate ? formatoFecha(v.birthDate) : undefined}
                                />
                                <SummaryRow
                                    label="Documento"
                                    value={
                                        v.documentExpiresAt
                                            ? `${nombreDelDocumento(v.documentType)} · vigente hasta ${formatoFecha(v.documentExpiresAt)}`
                                            : nombreDelDocumento(v.documentType)
                                    }
                                />
                            </dl>
                            {/* Sin panel de estado de la cuenta: solo puede volver leyendo `accountStatus`
                            real de la sesión, nunca el formulario. */}
                        </div>
                    )}

                    {/* El domicilio dejó de ser un renglón de solo lectura del resumen: el OCR del INE es
                    best-effort y así el cliente se iba sin domicilio. Aquí se ve y se puede agregar. */}
                    <div className="mx-auto mb-4 max-w-md text-left">
                        <AddressBlock
                            sessionId={sessionId}
                            title="Domicilio del cliente"
                            instruction="¿Dónde vive? Si no está, búscalo."
                            addresses={savedAddresses}
                            // Mientras se relee, la lista vieja no puede afirmar que falta el domicilio:
                            // el buscador abierto invita a capturar un duplicado del que ya está.
                            loading={profileFetching}
                            settled={savedAddresses.length > 0}
                            savedHint="Domicilio registrado en su expediente."
                            emptyHint="El domicilio que se capturó todavía no aparece en su expediente. Búscalo aquí para completarlo."
                            changeLabel="Agregar otro domicilio"
                        />
                    </div>

                    {/* Micro-paso: en la venta, los dos datos que el proveedor no devuelve; en la
                    apertura, las 6 del perfil juntas. */}
                    {isAccountOpening ? (
                        <div className="mx-auto max-w-md text-left">
                            <ProfileQuestions
                                value={openingAnswer}
                                onChange={changeOpeningAnswer}
                                hint={ACCOUNT_OPENING_PROFILE_HINT}
                                invalid={openingFailure?.missingFields}
                                invalidHint={ACCOUNT_OPENING_MISSING_FIELD}
                            />
                            {occupationOtherInput}
                            {openingFailure && (
                                <p
                                    role="alert"
                                    className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11.5px] leading-relaxed text-destructive"
                                >
                                    {openingFailure.message}
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="mx-auto max-w-md text-left">
                            <label
                                htmlFor="kyc-occupation"
                                className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                            >
                                ¿A qué se dedica?
                            </label>
                            <Select
                                id="kyc-occupation"
                                value={occ}
                                onChange={(val) => {
                                    setOcc(val as Occupation);
                                }}
                                options={OCCUPATION_OPTIONS}
                                placeholder="Selecciona una opción…"
                            />
                            {occupationOtherInput}
                            <label
                                htmlFor="kyc-monthly-income"
                                className="mb-2 mt-4 block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                            >
                                ¿Cuánto gana al mes?
                            </label>
                            <Select
                                id="kyc-monthly-income"
                                value={income}
                                onChange={(val) => {
                                    setIncome(val as MonthlyIncome);
                                }}
                                options={MONTHLY_INCOME_OPTIONS}
                                placeholder="Selecciona una opción…"
                            />
                        </div>
                    )}
                </div>
            </KycCard>
        );
    };

    return (
        <>
            {captureModal}
            {renderScreen()}
        </>
    );
}

/** Revisiones en la espera: nada antes de que el cliente empiece, chips mientras verifica. */
function WaitingStations({
    stage,
    subChecks,
    isFacematch,
}: {
    stage: KycAttemptStageEnum | null;
    subChecks: KycSubChecks | null | undefined;
    isFacematch: boolean;
}) {
    if (stage === KycAttemptStageEnum.AWAITING_START || stage === KycAttemptStageEnum.EXPIRED) {
        return null;
    }
    if (stage === KycAttemptStageEnum.STARTED || stage === KycAttemptStageEnum.INPUTS_COMPLETED) {
        return <KycStationChips subChecks={subChecks} isFacematch={isFacematch} />;
    }
    return <EstacionesKyc subChecks={subChecks} esFacematch={isFacematch} />;
}

/** El icono que identifica cada cosa que puede pedir el paso, de un vistazo. */
function RequestIcon({ request }: { request: Step02Request }) {
    if (request === 'PROOF_OF_ADDRESS') return <ReceiptText className="size-7" aria-hidden />;
    if (request === 'FACEMATCH') return <ScanFace className="size-7" aria-hidden />;
    return <IdCard className="size-7" aria-hidden />;
}

/**
 * Card del paso 02 — el head es el mismo en todos sus estados; el foot solo cuando hay acción.
 *
 * 🔴 El encabezado dice QUÉ se está pidiendo, no en qué paso vamos. Las tres pantallas del 02 se
 * parecen —icono redondo y dos modalidades—, y con un título fijo de "Verificación de identidad" el
 * vendedor no distinguía el KYC completo del facematch ni del comprobante: llegó a anunciarle la INE
 * a un cliente al que solo le faltaba un recibo.
 */
function KycCard({
    children,
    foot,
    request,
    sessionId,
    showExit = true,
}: {
    children: ReactNode;
    foot?: ReactNode;
    request: Step02Request;
    sessionId: string;
    /** Lo apaga la espera de la cuenta en sus primeros segundos. */
    showExit?: boolean;
}) {
    const copy = step02RequestCopy(request);
    const { data: session } = useWizardSession(sessionId);
    const badge = (
        <Badge variant={copy.badgeVariant} className="whitespace-nowrap">
            {copy.badge}
        </Badge>
    );
    return (
        <WizardCard>
            {session && hasAccountOpeningMark(session) ? (
                <WizardCardHead
                    eyebrow={ACCOUNT_OPENING_KYC_EYEBROW}
                    title={copy.title}
                    sub={copy.sub}
                    right={badge}
                />
            ) : (
                <WizardCardHead eyebrow="Inicio" title={copy.title} sub={copy.sub} right={badge} />
            )}
            <WizardCardBody>{children}</WizardCardBody>
            {/* El pie SIEMPRE existe. Antes solo se pintaba cuando el estado traía acción, así que
                la card cambiaba de forma entre los 12 estados y no había cómo volver ni cómo salir
                guardando. Las tres pantallas del 02 —KYC, facematch y comprobante— comparten esta
                card, así que las tres heredan las mismas salidas. */}
            <WizardFootNav
                back={<WizardBackLink sessionId={sessionId} step="otp" />}
                showExit={showExit}
            >
                {foot}
            </WizardFootNav>
        </WizardCard>
    );
}

/**
 * Error de `kyc/prepare` a la vista del vendedor. Son candados nuevos del silo, a propósito:
 * 500 `CONFIGURACION_FALTANTE` (el despliegue no tiene las variables de Metamap) y 409 (la sesión
 * todavía no resolvió la identidad Fiado — se arregla reintentando el paso 01). Antes fallaba mudo
 * y el cliente hacía el KYC completo para nada.
 */
function PrepareErrorNotice({ error }: { error: unknown }) {
    return (
        <QueryErrorNotice
            title="No se pudo iniciar la verificación"
            error={error}
            fallback="No se pudo preparar la verificación."
            hint="No sigas con la venta hasta resolverlo: sin verificación no se abre la cuenta."
        />
    );
}

/**
 * Falla al consultar la cuenta, a la vista del vendedor. Mismo formato que `PrepareErrorNotice`,
 * pero con el mensaje que corresponde: aquí lo que falló fue la lectura, no la apertura.
 */
function AccountErrorNotice({ error }: { error: unknown }) {
    return (
        <QueryErrorNotice
            title="No se pudo leer el estado de la cuenta"
            error={error}
            fallback="No se pudo consultar el estado de la cuenta."
        />
    );
}

/**
 * Falla al consultar el KYC, a la vista del vendedor: sin esto la espera sana y la consulta caída se
 * ven idénticas. No corta la espera ni ofrece reintentar — el poll repregunta solo.
 */
function KycPollErrorNotice({ error }: { error: unknown }) {
    return (
        <QueryErrorNotice
            title="No se pudo leer el estado de la verificación"
            error={error}
            fallback="No se pudo consultar cómo va la verificación."
            hint="Lo que ves puede estar atrasado. Seguimos consultando; si no se destraba, repórtalo a soporte."
        />
    );
}

/** Ítem de la lista "qué verifica el proveedor" — palomita en círculo verde suave. */
function CheckItem({ title, description }: { title: string; description: string }) {
    return (
        <div className="mb-2 flex items-start gap-3 text-[12.5px]">
            <span className="mt-0.5 flex size-5 flex-none items-center justify-center rounded-full bg-success-soft text-success">
                <Check className="size-3" strokeWidth={3} aria-hidden />
            </span>
            <span>
                <span className="block font-medium">{title}</span>
                <span className="block text-muted-foreground">{description}</span>
            </span>
        </div>
    );
}

/**
 * Renglón del resumen del INE: etiqueta caps a la izquierda, valor a 2 columnas (grid 3 del mock).
 *
 * Un valor ausente NO se pinta vacío. El OCR de una credencial es best-effort y cualquier campo puede
 * no haberse extraído; un renglón en blanco se lee como "todo bien" y el vendedor sigue con la venta
 * sin enterarse. Por eso el faltante se dice, y en tono muted para que no compita con los datos reales.
 */
function SummaryRow({
    label,
    value,
    mono,
    fallback = 'No se pudo leer',
}: {
    label: string;
    value?: string;
    mono?: boolean;
    fallback?: string;
}) {
    const vacio = !value?.trim();
    return (
        <div className="grid grid-cols-3 gap-2 text-[12px]">
            <dt className="text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </dt>
            <dd
                className={cn(
                    'col-span-2',
                    vacio ? 'italic text-muted-foreground' : 'font-medium',
                    mono && !vacio && 'font-mono font-normal',
                )}
            >
                {vacio ? fallback : value}
            </dd>
        </div>
    );
}

/**
 * Fecha ISO → `dd/mm/aaaa`. El silo la manda en ISO-8601 y "venc. 2033-12-31" en medio de una frase
 * en español se lee como un dato de sistema, no como una fecha.
 *
 * Se parte el string a mano en vez de usar `new Date(...)`: una fecha sola (`2033-12-31`) se
 * interpreta como UTC y en México (UTC-6) se corre un día hacia atrás al formatearla en local.
 */
function formatoFecha(iso: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}
